import { message } from 'antd'
import dayjs from 'dayjs'
import { useRolodexEdit } from 'features/fde'
import { CantileverReportDataResult } from 'pages/reporting/reports/rug/hooks/useCantileverReportData'
import {
  CoverSectionData,
  DataSheetSectionData,
  ImagesSectionData,
  MainInfo,
  RugFormDataState,
  RUGPrePopulatedData,
  SectionAddendum,
  SummarySectionData,
} from 'pages/reporting/reports/rug/rugReportData'
import {
  AssetDrawingsData,
  RevisionMetadata,
} from 'pages/reporting/shared/types'
import { convertDatesToDay } from 'pages/reporting/shared/utils/dateUtils'
import { useCallback, useEffect, useState } from 'react'
import {
  GeckoMeasurement,
  GeckoMeasurementJson,
  GeckoReportingMetadata,
  LockState,
  SignatureData,
  SignoffState,
} from 'types'

export function deserializeGeckoMeasurementsAndDates<T>(data: T): T {
  const isGeckoMeasurementJson = (
    obj: unknown
  ): obj is GeckoMeasurementJson => {
    if (obj === null || typeof obj !== 'object') return false

    // Check if the object has 'num' and 'unit' properties
    if (!('num' in obj) || !('unit' in obj)) return false

    // Get the object with the proper type to access properties
    const candidate = obj as Record<string, unknown>

    // Check if num is a number and unit is a string
    return (
      typeof candidate.num === 'number' && typeof candidate.unit === 'string'
    )
  }

  if (data === null || data === undefined) return data

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map((item) =>
      deserializeGeckoMeasurementsAndDates(item)
    ) as unknown as T
  }

  if (typeof data === 'string') {
    // Dayjs can't validate the UTC indicator Z,
    // so check for it, then remove it and try ISO8601 validating
    if (data.includes('Z')) {
      if (
        dayjs(
          data.slice(0, data.length - 1),
          'YYYY-MM-DDTHH:mm:ss.SSS',
          true
        ).isValid()
      ) {
        return dayjs(data) as unknown as T
      }
    }
  }

  // Handle objects
  if (typeof data === 'object') {
    // Check if this looks like a serialized GeckoMeasurement
    if (isGeckoMeasurementJson(data)) {
      // Recreate a proper GeckoMeasurement object
      return GeckoMeasurement.CreateFromJSON({
        num: data.num,
        unit: data.unit,
      }) as unknown as T
    }

    // Process each property recursively
    const result: Record<string, unknown> = {}
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        result[key] = deserializeGeckoMeasurementsAndDates(
          (data as Record<string, unknown>)[key]
        )
      }
    }
    return result as unknown as T
  }

  return data
}

export function getSelectedFormData(
  metadataEntryId: string,
  metadataEntries: readonly GeckoReportingMetadata[],
  prePopulatedData?: RUGPrePopulatedData
) {
  const currentEntry = metadataEntries.find(
    (entry) => entry.id === metadataEntryId
  )

  if (!currentEntry) {
    console.warn('Current metadata entry not found')
    return
  }

  const existingData = currentEntry.data as Record<string, unknown>

  const sectionInfo = existingData.section_info as Partial<RugFormDataState>

  // Helper function to merge stored data with prepopulated data
  // Prepopulated data is used as defaults, but stored data takes precedence
  const mergeWithPrePopulated = <T>(
    stored: T | undefined,
    prepopulated: Partial<T> | undefined
  ): T => {
    if (!prepopulated) return (stored || {}) as T
    return { ...prepopulated, ...stored } as T
  }

  const newFormData = {
    mainInfo: mergeWithPrePopulated<MainInfo>(
      sectionInfo.mainInfo as MainInfo,
      prePopulatedData
    ),
    // TODO: Add better typing for convertDatesToDay
    coverSectionData: convertDatesToDay(
      mergeWithPrePopulated<CoverSectionData>(
        sectionInfo.coverSectionData as CoverSectionData,
        prePopulatedData
      ) as unknown as Record<string, unknown>
    ) as unknown as CoverSectionData,
    summarySectionData: mergeWithPrePopulated<SummarySectionData>(
      sectionInfo.summarySectionData as SummarySectionData,
      prePopulatedData
    ),
    dataSheetSectionData: {
      ...mergeWithPrePopulated<DataSheetSectionData>(
        sectionInfo.dataSheetSectionData as DataSheetSectionData,
        prePopulatedData
      ),
    },
    imagesSectionData:
      (sectionInfo.imagesSectionData as ImagesSectionData) ||
      ({} as ImagesSectionData),
    assetDrawings: (sectionInfo.assetDrawings as AssetDrawingsData) || {
      images: {},
    },
    signoff: (existingData.signoff as SignoffState) || {
      lastRevision: 0,
    },
    revisionMetadata: (existingData.revisionMetadata as RevisionMetadata) || {
      revisionNumber: 1,
      createdAt: new Date().toISOString(),
      isPublished: false,
    },
    lockState: (existingData.lockState as LockState) || {
      isLocked: false,
      startedSignoff: false,
    },
    publishedPdfPath: (existingData.publishedPdfPath as string) || '',
    sectionAddendums: (sectionInfo.sectionAddendums as SectionAddendum[]) || [],
  }

  return newFormData
}

export const useFormData = (
  metadataEntryId: string,
  metadataEntries: readonly GeckoReportingMetadata[],
  isLoading: boolean,
  prePopulatedData?: CantileverReportDataResult['prePopulatedData']
) => {
  const [formData, setFormData] = useState<RugFormDataState>({
    mainInfo: {
      assetName: '',
      assetId: '',
      assetType: '',
      assetMaterial: '',
      customerName: '',
      customerLocation: '',
      inspectionTemperature: '',
      componentAxisStartLocations: {},
    },
    coverSectionData: {
      inspectionStartDate: '',
      inspectionEndDate: '',
    },
    summarySectionData: {
      inspectionResolution: {
        height: 0,
        width: 0,
        widthUnit: '',
        heightUnit: '',
      },
      deliverableResolution: {
        height: 0,
        width: 0,
        widthUnit: '',
        heightUnit: '',
      },
      coverageScope: '',
      corrosionBLUF: '',
    },
    dataSheetSectionData: {
      sensorSerialNumbers: {},
      inspectionSurface: '',
      surfaceCondition: '',
      coatingPresent: false,
      robots: [],
      calibrationBlocks: [],
      calibrationTimes: [],
      calibrationSteps: [],
    },
    imagesSectionData: {
      imageReportIds: [],
    },
    assetDrawings: {
      images: {},
    },
    signoff: {
      lastRevision: 0,
    },
    revisionMetadata: {
      revisionNumber: 1,
      createdAt: new Date().toISOString(),
      isPublished: false,
    },
    lockState: {
      isLocked: false,
      startedSignoff: false,
    },
    publishedPdfPath: '',
    sectionAddendums: [],
  })

  const { rolodexPatch } = useRolodexEdit()

  useEffect(() => {
    const newFormData = getSelectedFormData(
      metadataEntryId,
      metadataEntries,
      prePopulatedData
    )
    if (newFormData) {
      setFormData(newFormData)
    }
  }, [isLoading, metadataEntries, metadataEntryId, prePopulatedData])

  const updateFormData = useCallback((newData: Partial<RugFormDataState>) => {
    setFormData((prev) => ({ ...prev, ...newData }))
  }, [])

  const handleMainInfoFinish = useCallback(
    async (values: MainInfo) => {
      try {
        updateFormData({ mainInfo: values })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: { mainInfo: values },
            },
          },
        ])

        message.success('Main info saved successfully!')
      } catch {
        message.error('Failed to save main info')
      }
    },
    [updateFormData, metadataEntryId, rolodexPatch]
  )

  const handleCoverSectionFinish = useCallback(
    async (values: CoverSectionData) => {
      try {
        const storageValues = {
          ...values,
          inspectionStartDate: values.inspectionStartDate,
          inspectionEndDate: values.inspectionEndDate,
        }

        updateFormData({
          coverSectionData: values,
        })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: {
                coverSectionData: storageValues,
              },
            },
          },
        ])
        message.success('Cover section saved successfully!')
      } catch {
        message.error('Failed to save cover section')
      }
    },
    [metadataEntryId, updateFormData, rolodexPatch]
  )

  const handleSummarySectionFinish = useCallback(
    async (values: SummarySectionData) => {
      try {
        updateFormData({
          summarySectionData: values,
        })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: {
                summarySectionData: values,
              },
            },
          },
        ])
        message.success('Summary section saved successfully!')
      } catch (error) {
        console.error('Error saving summary section:', error)
        message.error('Failed to save summary section')
      }
    },
    [metadataEntryId, updateFormData, rolodexPatch]
  )

  const handleDataSheetSectionFinish = useCallback(
    async (values: DataSheetSectionData) => {
      try {
        // Handle any date conversions if needed
        const storageValues = {
          ...values,
          calibrationBlocks: values.calibrationBlocks || [],
          calibrationTimes: values.calibrationTimes || [],
        }

        updateFormData({ dataSheetSectionData: values })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: {
                dataSheetSectionData: storageValues,
              },
            },
          },
        ])

        message.success('Data sheet section saved successfully')
      } catch (error) {
        console.error('Failed to save data sheet section:', error)
        message.error('Failed to save data sheet section')
        throw error
      }
    },
    [metadataEntryId, updateFormData, rolodexPatch]
  )

  const handleImagesSectionFinish = useCallback(
    async (values: ImagesSectionData) => {
      try {
        updateFormData({ imagesSectionData: values })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: {
                imagesSectionData: values,
              },
            },
          },
        ])
        message.success('Images section saved successfully!')
      } catch {
        message.error('Failed to save images section')
      }
    },
    [metadataEntryId, updateFormData, rolodexPatch]
  )

  const handleAssetDrawingsFinish = useCallback(
    async (
      imagePath: string,
      imageType: 'heatmap' | 'no-heatmap' | 'heatmap-with-indications' | string,
      index?: number,
      imageCaption?: string
    ) => {
      try {
        const updatedImages = {
          ...formData.assetDrawings.images,
          [imageType]: {
            imagePath,
            imageCaption,
            type: imageType,
            index,
          },
        }

        updateFormData({
          assetDrawings: {
            images: updatedImages,
          },
        })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: {
                assetDrawings: {
                  images: updatedImages,
                },
              },
            },
          },
        ])
        message.success('Asset drawing saved successfully!')
      } catch {
        message.error('Failed to save asset drawing')
      }
    },
    [
      metadataEntryId,
      formData.assetDrawings.images,
      updateFormData,
      rolodexPatch,
    ]
  )

  const handleSectionAddendumsFinish = useCallback(
    async (sectionAddendums: SectionAddendum[]) => {
      try {
        updateFormData({ sectionAddendums })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: {
                sectionAddendums,
              },
            },
          },
        ])

        message.success('Section addendums saved successfully')
      } catch (error) {
        console.error('Failed to save section addendums:', error)
        message.error('Failed to save section addendums')
        throw error
      }
    },
    [updateFormData, metadataEntryId, rolodexPatch]
  )

  const handleSignoffFinish = useCallback(
    async (signatureData: SignatureData): Promise<SignoffState> => {
      try {
        const updatedSignoff: SignoffState = {
          ...(formData.signoff || { lastRevision: 0 }),
          [signatureData.role === 'supervisor'
            ? 'supervisorSignoff'
            : 'reviewerSignoff']: signatureData,
        }

        // Increment revision if reviewer is signing
        if (
          signatureData.role === 'reviewer' &&
          updatedSignoff.supervisorSignoff
        ) {
          updatedSignoff.lastRevision = (updatedSignoff.lastRevision || 0) + 1
        }

        // Update Rolodex first
        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              signoff: updatedSignoff,
            },
          },
        ])

        // Then update local state
        updateFormData({
          signoff: updatedSignoff,
        })

        return updatedSignoff
      } catch (error) {
        console.error('Failed to save signoff:', error)
        throw error
      }
    },
    [formData.signoff, metadataEntryId, rolodexPatch, updateFormData]
  )

  return {
    formData,
    updateFormData,
    handleMainInfoFinish,
    handleCoverSectionFinish,
    handleSummarySectionFinish,
    handleDataSheetSectionFinish,
    handleImagesSectionFinish,
    handleAssetDrawingsFinish,
    handleSectionAddendumsFinish,
    handleSignoffFinish,
  }
}
